// A station's identity in eRound (CWA-154 finding 4).
//
// eRound holds exactly ONE identity per charging station — its technical name —
// used both as the station's name and as the identity the hardware presents when
// it connects. The Back Office used to show a second, differently formatted
// value ("DE-COC-1005") that matched nothing in eRound, so a customer following
// the configuration email could not connect at all.
//
// Newly created credentials now use the station id verbatim. Rows created before
// that fix still hold the old value and are corrected only when an agent
// re-saves them — which is what these helpers make visible.

import { ProspectStation } from "../types/onboarding";

const normalise = (value?: string | null): string => (value ?? "").trim();

/**
 * Whether this station's stored identity is one eRound would not match.
 *
 * Compared case-sensitively: eRound matches the technical name exactly, so a row
 * differing only in case is just as broken as one differing entirely. A station
 * with no access data recorded is never stale — there is nothing stored to be
 * wrong — and neither is one whose identity was never recorded.
 */
export const isStaleStationIdentity = (station: ProspectStation): boolean => {
  if (!station?.has_credentials) return false;
  const stored = normalise(station.charge_point_id);
  const expected = normalise(station.station_id);
  if (!stored || !expected) return false;
  return stored !== expected;
};

/** The identity a stale station should be re-saved with. */
export const recommendedStationIdentity = (
  station: ProspectStation
): string => normalise(station?.station_id);

/** How many of these stations need re-saving, for one prospect-level summary. */
export const staleIdentityCount = (
  stations: ProspectStation[] | null | undefined
): number => (stations ?? []).filter(isStaleStationIdentity).length;
